import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import cx from 'classnames';
import { Page } from '@accedo/vdkweb-tv-ui';
import {
  getFeatureSteps,
  getSensicalTvConfig,
} from '#/redux/modules/accedoOne/selectors';
import { sendScreenView } from '#/redux/modules/firestore/actions';
import { authFlowRoutes } from '#/utils/routes';
import { authScreenNames } from '#/config/constants';

import { WELCOME } from '#/utils/navigationHelper';
import { FocusButton, FocusDiv } from '#/widgets/ui';
import {
  welcomeTitleWeb,
  welcomeTitleTv,
  welcomeTitleShort,
} from '#/config/strings';
import WelcomeAnimals from '#/static/images/logos/welcome-animals.png';
import BgFeatureTour from '#/static/images/background-images/BgFeatureTour.png';
import { isWorkstation, isMobile } from '#/utils/componentStyleConfig';
import styles from './Welcome.scss';
import authStyles from '../authflow.scss';
import { SignInButton } from '../SignInButton';

const { WELCOME: WELCOME_MESSAGE, SIGN_IN, SIGN_UP } = authScreenNames;

const TourCard = ({ displayText, subtitle, image: [{ fileUrl: url }] }) => {
  const subtitles = subtitle.split('\n\n');
  return (
    <div className={styles.tourCard}>
      <img src={url} alt="Step" />
      <div className={styles.caption}>
        <h3 className={styles.title}>{displayText}</h3>
        {subtitles.map((sub, i) => (
          <p key={i} className={styles.subtitle}>
            {sub}
          </p>
        ))}
      </div>
    </div>
  );
};
TourCard.propTypes = {
  displayText: PropTypes.string,
  subtitle: PropTypes.string,
  image: PropTypes.array,
};

const FeatureSteps = () => {
  const featureSteps = useSelector(getFeatureSteps);
  return featureSteps.map((step, i) => <TourCard key={i} {...step} />);
};

const Welcome = ({ history }) => {
  const dispatch = useDispatch();
  const { logoUri = '' } =
    useSelector(state => getSensicalTvConfig(state)) || {};

  useEffect(() => {
    // Analytics can wait until first paint/idle so onboarding UI renders faster.
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(() => {
        dispatch(sendScreenView(WELCOME_MESSAGE));
      });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = setTimeout(() => {
      dispatch(sendScreenView(WELCOME_MESSAGE));
    }, 0);
    return () => clearTimeout(timeoutId);
  }, []);

  const webComponent = (
    <div className={cx(authStyles.authFlow, styles.welcome)}>
      <div className={cx(authStyles.content, styles.content)}>
        <img
          className={styles.logoWebLayout}
          src={logoUri?.replace('Black', 'Green')}
          alt="Sensical"
        />
        <h1 className={authStyles.welcomeTitle}>{welcomeTitleWeb}</h1>
        <h1
          className={`${authStyles.welcomeTitle} ${authStyles.welcomeTitleShort}`}
        >
          {welcomeTitleShort}
        </h1>
        <img
          className={styles.logoWebLayoutShort}
          src={logoUri?.replace('Black', 'Green')}
          alt="Sensical"
        />
        <div className={styles.tourCardContainer}>
          <FeatureSteps />
          <img
            src={WelcomeAnimals}
            className={styles.animals}
            alt="Fuzzy Animals"
          />
        </div>
        <div className={cx(authStyles.buttonsGroup, styles.buttonsGroup)}>
          <SignInButton className={styles.signUpBtn} />
          {!isMobile() && (
            <div>
              Have an account?{'  '}
              <Link to={authFlowRoutes.SIGN_IN} className={styles.signIn}>
                {SIGN_IN}
              </Link>
            </div>
          )}
        </div>
      </div>
      <div className={styles.background} alt="Feature Background" />
    </div>
  );

  const tvComponent = (
    <Page
      className={cx(authStyles.authFlow, styles.welcome)}
      autofocus
      nav={{ id: WELCOME.PAGE, forwardFocus: WELCOME.CONTAINER }}
    >
      <FocusDiv
        className={cx(authStyles.content, styles.contentTv)}
        nav={{
          id: WELCOME.CONTAINER,
          forwardFocus: WELCOME.SIGNUP_BUTTON,
        }}
      >
        <img
          className={styles.logoTvLayout}
          src={logoUri?.replace('Black', 'Green')}
          alt="Sensical"
        />
        <h1 className={authStyles.welcomeTitleTv}>{welcomeTitleTv}</h1>
        <div className={styles.tourCardContainer}>
          <FeatureSteps />
        </div>
        <div className={authStyles.buttonsGroupTv}>
          <FocusButton
            nav={{
              id: WELCOME.SIGNUP_BUTTON,
              nextright: WELCOME.SIGNIN_BUTTON,
              parent: WELCOME.CONTAINER,
            }}
            isTv
            primary
            className={styles.onboardTvBtn}
            onClick={() => {
              history.push(authFlowRoutes.SIGN_UP);
            }}
            ariaLabel="Sign up"
          >
            {SIGN_UP}
          </FocusButton>
          <FocusButton
            className={styles.onboardTvBtn}
            secondary
            isTv
            nav={{
              id: WELCOME.SIGNIN_BUTTON,
              nextleft: WELCOME.SIGNUP_BUTTON,
              parent: WELCOME.CONTAINER,
            }}
            onClick={() => {
              history.push(authFlowRoutes.SIGN_IN);
            }}
            ariaLabel="Sign in"
          >
            {SIGN_IN}
          </FocusButton>
        </div>
      </FocusDiv>
      <img
        src={BgFeatureTour}
        className={styles.backgroundTv}
        alt="Feature Background"
      />
    </Page>
  );

  return isWorkstation() ? webComponent : tvComponent;
};

Welcome.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

export default Welcome;
